import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

export type ScheduleType = 'Manual' | 'Interval' | 'Daily' | 'Cron';

export interface EditSchedulePrefill {
  id: string;
  sourceName: string;
  scheduleType: ScheduleType;
  interval?: string;
  cron?: string;
  isActive: boolean;
}

export interface EditSchedulePayload {
  id: string;
  scheduleType: ScheduleType;
  interval?: string;
  cron?: string;
  isActive: boolean;
}

interface Props {
  onClose: () => void;
  onSubmit: (updated: EditSchedulePayload) => void;
  prefill: EditSchedulePrefill;
}

const EditScheduleModal: React.FC<Props> = ({ onClose, onSubmit, prefill }) => {
  const [scheduleType, setScheduleType] = useState<ScheduleType>(prefill.scheduleType);
  const [interval, setInterval] = useState(prefill.interval ?? '');
  const [cron, setCron] = useState(prefill.cron ?? '');
  const [isActive, setIsActive] = useState(prefill.isActive);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleSubmit = () => {
    onSubmit({
      id: prefill.id,
      scheduleType,
      interval: scheduleType === 'Interval' ? interval : undefined,
      cron: scheduleType === 'Cron' ? cron : undefined,
      isActive,
    });
    onClose();
  };

  const handleScheduleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value as ScheduleType;
    if (v === 'Manual' || v === 'Interval' || v === 'Daily' || v === 'Cron') {
      setScheduleType(v);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex justify-center items-start pt-20 px-4">
      <div className="bg-neutral-900 w-full max-w-xl rounded-xl shadow-lg border border-neutral-800">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-800">
          <h2 className="text-white text-lg font-semibold">Configure Schedule for: {prefill.sourceName}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-5 text-sm text-white">
          <div>
            <label className="text-gray-400 block mb-1">Schedule Type</label>
            <select
              value={scheduleType}
              onChange={handleScheduleTypeChange}
              className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
            >
              <option value="Manual">Manual Only</option>
              <option value="Interval">Every X Minutes</option>
              <option value="Daily">Daily</option>
              <option value="Cron">Custom Cron</option>
            </select>
          </div>

          {scheduleType === 'Interval' && (
            <div>
              <label className="text-gray-400 block mb-1">Interval (minutes)</label>
              <input
                value={interval}
                onChange={(e) => setInterval(e.target.value)}
                placeholder="e.g. 15"
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
              />
            </div>
          )}

          {scheduleType === 'Cron' && (
            <div>
              <label className="text-gray-400 block mb-1">Cron Expression</label>
              <input
                value={cron}
                onChange={(e) => setCron(e.target.value)}
                placeholder="e.g. 0 2 * * *"
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">Cron format: min hour day month weekday</p>
            </div>
          )}

          <div className="flex items-center gap-2 mt-3">
            <input
              type="checkbox"
              checked={isActive}
              onChange={() => setIsActive(!isActive)}
              className="accent-indigo-500"
            />
            <label className="text-sm text-gray-300">Mark as active</label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-800 bg-neutral-950">
          <button onClick={onClose} className="text-gray-400 hover:text-white text-sm">Cancel</button>
          <button
            onClick={handleSubmit}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-md text-sm shadow"
          >
            Save Schedule
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditScheduleModal;
